// Scope helpers — the FE-side utilities over the contract's `Scope` model. The contract defines the
// shape (`Scope`, `ScopeLevel`, `SCOPE_LEVELS`); this module adds the small pure functions the engine
// needs: the fleet root, child-level lookup, breadcrumb lineage, equality, and a short serialization
// for query keys. No React here — kept pure so hooks and components can share it.

import { SCOPE_LEVELS, type Scope, type ScopeLevel } from "@busright/data-contract";

// The top of the hierarchy — everything, no narrowing. The default scope on first load.
export const FLEET_SCOPE: Scope = { level: "fleet", label: "Entire fleet" } as const;

// Human label for a scope level (for breadcrumbs / the switcher), e.g. "district" → "District".
const LEVEL_LABELS: Record<ScopeLevel, string> = {
  fleet: "Fleet",
  state: "State",
  district: "District",
  account: "Account",
  device: "Device",
  order: "Order",
  route: "Route",
  sku: "SKU",
};

export const scopeLevelLabel = (level: ScopeLevel): string => LEVEL_LABELS[level];

// The next finer level below `level`, or null at the leaf (`sku`). Lets a view know what grain its
// drill rows will come back at without hardcoding the sequence.
export const childLevel = (level: ScopeLevel): ScopeLevel | null => {
  const idx = SCOPE_LEVELS.indexOf(level);
  if (idx < 0 || idx >= SCOPE_LEVELS.length - 1) {
    return null;
  }
  return SCOPE_LEVELS[idx + 1];
};

// Two scopes are the same target when their level + id match. Labels/parents are presentation only.
export const scopeEquals = (a: Scope, b: Scope): boolean =>
  a.level === b.level && (a.id ?? null) === (b.id ?? null);

// A short, stable key for a scope (for React Query keys / memo deps). Fleet has no id.
export const scopeKey = (scope: Scope): string => `${scope.level}:${scope.id ?? "*"}`;

// Walk a scope's `parent` chain into a root→current ordered lineage for breadcrumbs.
export const scopeLineage = (scope: Scope): readonly Scope[] => {
  const chain: Scope[] = [];
  let cursor: Scope | undefined = scope;
  // Defensive cap — contract says chains are shallow (≤3–4) but never trust a cycle.
  let guard = 0;
  while (cursor && guard < 16) {
    chain.unshift(cursor);
    cursor = cursor.parent;
    guard += 1;
  }
  return chain;
};

// The human name to show for a scope: its label, else a level-qualified id, else the level name.
export const scopeDisplayName = (scope: Scope): string => {
  if (scope.label) {
    return scope.label;
  }
  if (scope.id) {
    return `${scopeLevelLabel(scope.level)} ${scope.id}`;
  }
  return scopeLevelLabel(scope.level);
};
